import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import classnames from 'classnames/bind'
import { Modal, Text, Icon, Button } from '@adc/ui-core'
import { FiltersModel } from '../../models'
import { InvestorPreferences } from '../../util'
import UserService from '../../shared/services/user'
import {
  gtmNewUserOnboardingInit,
  gtmNewUserOnboardingClick,
  gtmNewUserOnboardingExit,
} from '../../shared/services/tracking'
import reducers from './reducers'
import * as actions from './actions'
import * as actionTypes from './actionTypes'
import styles from './styles.scss'
import { useApplicationContext } from '../auctionApplication/context'
const cx = classnames.bind(styles)

const baseElmId = 'new_user_onboarding_modals'
const INVESTOR_PREFERENCES_ENDPOINT = '/apps/api/investor/preferences'
const QUESTION_SETS = InvestorPreferences.IP_OBJECT()
const PROPERTY_TYPE_QUESTION =
  InvestorPreferences.IP_QUESTION_CODE.INV_PREF_PROPERTY_TYPE
const OCCUPIED_PROPERTIES_QUESTION =
  InvestorPreferences.IP_QUESTION_CODE.INV_PREF_OCCUPIED_PROPERTIES

const ThankYouPage = ({ isPDP, onClick }) => (
  <>
    <Text color='gray' size='large'>
      Look for updates and recommendations just for you
    </Text>
    <Text color='gray' className={cx('u-mt-4')} size='large'>
      {isPDP
        ? 'Ready to build your wealth?'
        : 'Ready to discover our exclusive properties?'}
    </Text>
    <div className={cx('u-mt-4')}>
      <Button
        role='primary'
        onClick={onClick}
        elmId={`${baseElmId}_thank_you_cta`}
      >
        {' '}
        {isPDP ? `Let's Go` : 'Search Now'}{' '}
      </Button>
    </div>
    <Text className={cx('u-mt-6')} color='gray'>
      <a href='/dashboard/preferences'>Edit your preferences</a> in your
      dashboard at anytime.
    </Text>
  </>
)

ThankYouPage.propTypes = {
  isPDP: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
}

const OnboardingButton = ({ elmId, code, option, onClick, isActive }) => {
  const _onClick = () => {
    gtmNewUserOnboardingClick(option['data-elm-id'])
    onClick(code, option.value)
  }

  return (
    <div
      className={cx('onboarding-large-button', isActive && 'active')}
      data-elm-id={`${elmId}_${option['data-elm-id']}`}
      onClick={_onClick}
    >
      <div className={cx('large-button-icon')}>
        {option.icon ? (
          <Icon
            name={option.icon}
            iconStyle='light'
            data-elm-id={`${elmId}_${option['data-elm-id']}_close`}
            color='gray'
            size='big'
          />
        ) : (
          <img src={option.img} />
        )}
      </div>
      <div className={cx('large-button-text', 'u-mr-4')}>
        <div className={cx('u-mb-1')}>
          <Text weight='heavy' color='gray'>
            {option.label}
          </Text>
        </div>
        <Text size='tiny' color='gray'>
          {option.content}
        </Text>
      </div>
    </div>
  )
}

OnboardingButton.propTypes = {
  elmId: PropTypes.string,
  code: PropTypes.string,
  option: PropTypes.object,
  onClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
}

const updateSavedSearches = (savedSearches, question) => {
  const answerCode = get(question, 'answer.value[0].response_code')
  return savedSearches.map(savedSearch => {
    savedSearch.parameters = {
      ...InvestorPreferences.assetType(answerCode),
      ...InvestorPreferences.occupancyStatus(answerCode),
      ...savedSearch.parameters,
    }
    return savedSearch
  })
}

const NewUserOnboardingModals = ({
  step,
  closeModal,
  nextStep,
  back,
  user,
  savedSearches,
  elmId = baseElmId,
  isPDP = false,
  skippable = true,
}) => {
  const { actions } = useApplicationContext()
  const onClose = () => {
    gtmNewUserOnboardingExit(step)
    closeModal()
  }
  const navigateToSavedSearch = () => {
    // This is safe because interested areas is a required field
    const params = savedSearches[0].parameters
    const model = new FiltersModel(params)
    const redirectUrl = model.urlValue
    window.location.assign(`/residential/${redirectUrl}`)
  }

  const onboardingQuestions = [
    QUESTION_SETS[PROPERTY_TYPE_QUESTION],
    QUESTION_SETS[OCCUPIED_PROPERTIES_QUESTION],
    {
      title: `You're All Set`,
      skippable: false,
      component: (
        <ThankYouPage
          isPDP={isPDP}
          onClick={isPDP ? onClose : navigateToSavedSearch}
        />
      ),
    },
  ]

  if (step <= 0 || step > onboardingQuestions.length) return null

  useEffect(() => gtmNewUserOnboardingInit(skippable), [skippable])
  useEffect(() => {
    // Hides preference nudge when on the last page
    if (step === onboardingQuestions.length) {
      actions.onOnboardingComplete()
    }
  }, [step, actions.onOnboardingComplete])

  const [selectedOptions, setSelectedOptions] = useState({
    [PROPERTY_TYPE_QUESTION]: null,
    [OCCUPIED_PROPERTIES_QUESTION]: null,
  })
  const questionIndex = step - 1
  const currentQuestion = onboardingQuestions[questionIndex]
  const { label } = currentQuestion

  const selectOption = async (code, value) => {
    setSelectedOptions({
      ...selectedOptions,
      [code]: value,
    })
    const questionSets = InvestorPreferences.IP_OBJECT()
    const questionValue = { [code]: value }
    const questionSet = InvestorPreferences.formToStore(
      questionSets[code],
      questionValue
    )
    const parsedQuestionSet = [InvestorPreferences.apiValue(questionSet)]
    nextStep()
    try {
      await UserService.investorPreferences(
        INVESTOR_PREFERENCES_ENDPOINT,
        parsedQuestionSet
      )
      await UserService.createSavedSearches(
        user.user_id,
        updateSavedSearches(savedSearches, questionSet)
      )
    } catch (e) {
      // What do we do when we error? Do we really want to show the user an error right after creating an account?
      console.error(e)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      closeIcon={skippable ? undefined : null} // no close icon or default close icon...
      closeOnDimmerClick={false}
      mobileTitleSkin='light'
      size='tiny'
      closeOnEscape={skippable}
      elmId={`${elmId}_modal`}
    >
      <span className={cx('onboarding-modal')}>
        <Modal.Header>
          <div className={cx('header')}>
            {step > 1 && !currentQuestion.hideBack && (
              <span
                className={cx('back-button')}
                data-elm-id={`${elmId}_back_button`}
              >
                <Icon
                  name='angle-left'
                  iconStyle='light'
                  color='gray'
                  size='large'
                  onClick={back}
                />
              </span>
            )}
            <span className={cx('steps')} data-elm-id={`${elmId}_steps`}>
              <Text color='gray'>
                {step} of {onboardingQuestions.length}
              </Text>
            </span>
          </div>
        </Modal.Header>
      </span>

      <Modal.Content>
        <div
          className={cx('onboarding-modal')}
          data-elm-id={`${elmId}_content`}
        >
          <div className={cx('u-mb-4')}>
            <Text size='huge' color='success' weight='normal'>
              {currentQuestion.title.replace('%s', user.first_name)}
            </Text>
          </div>
          <h2 data-elm-id={`${elmId}_question`}>{label}</h2>
          <div
            className={cx('form-pages')}
            style={{
              transform: `translateX(${
                questionIndex * (-100 / onboardingQuestions.length)
              }%)`,
              width: `${onboardingQuestions.length * 100}%`,
            }}
          >
            {onboardingQuestions.map(
              (
                {
                  options = [],
                  code,
                  component,
                  skippable: questionSkippable = true,
                },
                i
              ) => (
                <div
                  className={cx('form-page', 'large', {
                    hidden: i !== questionIndex,
                  })}
                  style={{ width: `${100 / onboardingQuestions.length}%` }}
                  key={i}
                >
                  {options.map((option, i) => (
                    <OnboardingButton
                      elmId={`${elmId}_option`}
                      key={`option_${i}`}
                      onClick={selectOption}
                      option={option}
                      code={code}
                      isActive={selectedOptions[code] === option.value}
                    />
                  ))}
                  {component}
                  {skippable && questionSkippable && (
                    <div className={cx('u-mt-2')} data-elm-id={`${elmId}_skip`}>
                      <a onClick={onClose}>Skip</a>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </Modal.Content>
    </Modal>
  )
}
NewUserOnboardingModals.propTypes = {
  step: PropTypes.number.isRequired,
  savedSearches: PropTypes.array.isRequired,
  isPDP: PropTypes.bool.isRequired,
  nextStep: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  back: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  skippable: PropTypes.bool,
  elmId: PropTypes.string,
}

const mapStoreToProps = store => ({
  step: store.newUserOnboarding.step,
  savedSearches: store.newUserOnboarding.savedSearches,
  user: store.user,
})

const mapDispatchToProps = dispatch => ({
  nextStep: () => dispatch(actions.addNewUserOnboarding(1)),
  back: () => dispatch(actions.addNewUserOnboarding(-1)),
  closeModal: () => dispatch(actions.setNewUserOnboarding(0)),
})

NewUserOnboardingModals.reducers = reducers
NewUserOnboardingModals.actions = actions
NewUserOnboardingModals.actionTypes = actionTypes

export default connect(
  mapStoreToProps,
  mapDispatchToProps
)(NewUserOnboardingModals)
